import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import AppContext from '../../../App/AppContext';
import { TextBox, SecondaryButton, DialogContents, DialogTitles, Dialogs } from '../../../Core/FormInput';
import '../../../Core/Modal/model.css';
import Grid from '@material-ui/core/Grid';
import FormLabel from '@material-ui/core/FormLabel';
import { DataGrid, GridOverlay } from '@material-ui/data-grid';
import { addWorkflowStepsListOptions, getWorkflowListOps } from '../../../Core/Service/workflowList-service';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import Tooltip from '@material-ui/core/Tooltip';
import { IconButton, CircularProgress } from '@material-ui/core';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import Box from '@material-ui/core/Box';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import { SketchPicker } from 'react-color';

import '../commonStyle.scss';

const popover = {
    position: 'absolute',
    zIndex: '2'
};
const cover = {
    position: 'fixed',
    top: '0px',
    right: '0px',
    bottom: '0px',
    left: '0px'
};

const presetColors = ['rgb(255, 255, 0)', 'rgb(0,0,255)', 'rgb(0,255,0)', 'rgb(255,0,0)'];

const WorkflowStepsListOptions = (props) => {
    const { showToast, hideModal } = useContext(AppContext);

    const [state, setState] = useState({
        Description: '',
        rows: [],
        listName: '',
        isUpdate: false,
        optionID: null,
        backgroundColour: '#5cb85c',
        foregroundColour: '#ffffff',
        displayBgColorPicker: false,
        displayFgColorPicker: false,
        showLoader: false
    });

    const Id = props.WorkflowStepsListDetails;

    const pullWorkflowAndUpdateState = useCallback(async () => {
        setState((st) => ({ ...st, showLoader: true }));
        let result = await getWorkflowListOps(Id);
        if (result.success) {
            setState((st) => ({
                ...st,
                showLoader: false,
                rows: result.data.listOptions.map((w) => ({
                    ...w,
                    id: w.listOptionId,
                    MarkForDelete: false,
                    backgroundColour: w.backgroundColour,
                    foregroundColour: w.foregroundColour
                })),
                listName: result.data.listName
            }));
        } else {
            setState((st) => ({ ...st, showLoader: false }));
            showToast(result.message);
        }
    }, [Id, showToast]);

    const handleUpdateOptions = useCallback((row) => {
        setState((st) => ({
            ...st,
            Description: row.listOptionName,
            isUpdate: true,
            optionID: row.id,
            backgroundColour: row.backgroundColour || '#5cb85c',
            foregroundColour: row.foregroundColour || '#ffffff'
        }));
    }, []);

    const handleDeletePart = useCallback(async (row) => {
        const id = row.id;
        setState((st) => {
            const updatedRows = st.rows.map((item) => {
                if (item.id === id) {
                    return { ...item, MarkForDelete: true };
                }
                return item;
            });
            const apiPayload = updatedRows.map((c) => ({
                listOptionId: c.listOptionId,
                listOptionName: c.listOptionName,
                MarkForDelete: c.MarkForDelete,
                backgroundColour: c.backgroundColour,
                foregroundColour: c.foregroundColour
            }));

            setTimeout(async () => {
                setState(st => ({ ...st, showLoader: true }));
                let res = await addWorkflowStepsListOptions(Id, apiPayload);
                if (res.success) {
                    showToast(res.message || 'Option deleted successfully');
                    pullWorkflowAndUpdateState();
                } else {
                    setState(st => ({ ...st, showLoader: false }));
                    showToast(res.message || 'Failed to delete option');
                    pullWorkflowAndUpdateState();
                }
            }, 0);

            return {
                ...st,
                rows: updatedRows
            };
        });
    }, [Id, showToast, pullWorkflowAndUpdateState]);

    const handleAddOrUpdateOption = useCallback(async () => {
        if (!state.Description.trim()) {
            showToast('Option Name is required');
            return;
        }

        let isDuplicate = false;
        setState((st) => {
            const trimmedDesc = st.Description.trim().toLowerCase();
            const activeRows = st.rows.filter(r => r.MarkForDelete !== true && r.id !== st.optionID);
            isDuplicate = activeRows.some(r => r.listOptionName.trim().toLowerCase() === trimmedDesc);
            
            if (isDuplicate) {
                setTimeout(() => {
                    showToast('Duplicate Option Name is not allowed');
                }, 0);
                return st;
            }

            let updatedRows = [];
            if (st.isUpdate) {
                updatedRows = st.rows.map((item) => {
                    if (item.id === st.optionID) {
                        return {
                            ...item,
                            listOptionName: st.Description.trim(),
                            backgroundColour: st.backgroundColour,
                            foregroundColour: st.foregroundColour
                        };
                    }
                    return item;
                });
            } else {
                const newOption = {
                    listOptionId: 0,
                    listOptionName: st.Description.trim(),
                    MarkForDelete: false,
                    backgroundColour: st.backgroundColour || '#5cb85c',
                    foregroundColour: st.foregroundColour || '#ffffff',
                    id: st.rows.length === 0 ? 1 : Math.max(...st.rows.map((c) => c.id)) + 1
                };
                updatedRows = [...st.rows, newOption];
            }

            const apiPayload = updatedRows.map((c) => ({
                listOptionId: c.listOptionId,
                listOptionName: c.listOptionName,
                MarkForDelete: c.MarkForDelete,
                backgroundColour: c.backgroundColour,
                foregroundColour: c.foregroundColour
            }));

            setTimeout(async () => {
                setState(st => ({ ...st, showLoader: true }));
                let res = await addWorkflowStepsListOptions(Id, apiPayload);
                if (res.success) {
                    showToast(res.message || (st.isUpdate ? 'Option updated successfully' : 'Option added successfully'));
                    setState((st2) => ({
                        ...st2,
                        Description: '',
                        isUpdate: false,
                        optionID: null,
                        backgroundColour: '#5cb85c',
                        foregroundColour: '#ffffff',
                        displayBgColorPicker: false,
                        displayFgColorPicker: false
                    }));
                    pullWorkflowAndUpdateState();
                } else {
                    setState(st => ({ ...st, showLoader: false }));
                    showToast(res.message || 'Failed to save option');
                }
            }, 0);

            return {
                ...st,
                rows: updatedRows
            };
        });
    }, [Id, showToast, pullWorkflowAndUpdateState, state]);

    const Arrow = useCallback(({ row }) => {
        return (
            <>
                <Tooltip title="Update Option" aria-label="add">
                    <IconButton size="small" onClick={() => handleUpdateOptions(row)}>
                        <EditOutlinedIcon color="secondary" fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete Option" aria-label="add">
                    <IconButton size="small" onClick={() => handleDeletePart(row)}>
                        <DeleteOutlineIcon className="SetButtonColor" fontSize="small" />
                    </IconButton>
                </Tooltip>
            </>
        );
    }, [handleUpdateOptions, handleDeletePart]);

    const columns = useMemo(() => [
        {
            field: 'id',
            headerName: 'ID',
            width: 130,
            sortable: false,
            hide: true
        },
        {
            field: '',
            headerName: 'Action',
            sortable: false,
            width: 100,
            renderCell: (params) => <Arrow row={params.row} />
        },
        {
            field: 'listOptionName',
            headerName: 'Name',
            flex: 1,
            renderCell: (params) => {
                const row = params.row;
                return (
                    <div
                        style={{
                            backgroundColor: row.backgroundColour || '#5cb85c',
                            color: row.foregroundColour || '#ffffff',
                            width: 'calc(100% + 20px)',
                            marginLeft: '-10px',
                            marginRight: '-10px',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            fontSize: '11px',
                            letterSpacing: '0.05em',
                            boxSizing: 'border-box',
                            paddingLeft: '20px',
                            paddingRight: '20px'
                        }}
                    >
                        {row.listOptionName}
                    </div>
                );
            }
        }
    ], [Arrow]);

    useEffect(() => {
        pullWorkflowAndUpdateState();
    }, [Id]);

    const fieldChange = useCallback((e) => {
        const { name, value } = e.target;
        setState((st) => ({ ...st, [name]: value }));
    }, []);

    const CustomNoRowsOverlay = useCallback(() => {
        return (
            <GridOverlay>
                <div>No options available</div>
            </GridOverlay>
        );
    }, []);

    const handleClose = () => {
        props.onFormSubmit(false);
        hideModal();
    };

    const activeRows = useMemo(() => {
        return state.rows.filter((item) => item.MarkForDelete !== true);
    }, [state.rows]);

    return (
        <Dialogs open={true} onClose={handleClose} maxWidth="md" fullWidth>
            <div style={{ margin: '10px auto', width: '100%', position: 'relative', overflowX: 'hidden' }}>
                {state.showLoader && (
                    <div style={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 10,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <CircularProgress color="secondary" />
                    </div>
                )}
                <Box display="flex" alignItems="center" style={{ padding: '0 24px' }}>
                    <Box flexGrow={1}>
                        <DialogTitles style={{ padding: '8px 0' }}>Workflow Steps List Option</DialogTitles>
                    </Box>
                    <Box>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
                <DialogContents style={{ padding: '8px 24px 24px 24px' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} style={{ marginTop: '-14px', marginBottom: '8px' }}>
                            <Typography color="secondary">List Name - {state.listName}</Typography>
                        </Grid>
                    </Grid>

                    <div
                        style={{
                            display: 'flex',
                            gap: '12px',
                            alignItems: 'flex-start',
                            flexWrap: 'wrap',
                            width: '100%',
                            marginBottom: '16px'
                        }}
                    >
                        <div style={{ flex: '1 1 200px' }}>
                            <FormLabel style={{ display: 'block', float: 'none', marginBottom: '4px', fontSize: '0.75rem' }}>Option Name</FormLabel>
                            <TextBox onChange={fieldChange} value={state.Description} name="Description" size="small" margin="none" />
                        </div>

                        <div style={{ flex: '0 0 140px', position: 'relative' }}>
                            <FormLabel style={{ display: 'block', float: 'none', marginBottom: '4px', fontSize: '0.75rem' }}>Background Color</FormLabel>
                            <div
                                style={{
                                    padding: '8px',
                                    background: '#fff',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    height: '38px',
                                    boxSizing: 'border-box'
                                }}
                                onClick={() => setState(st => ({ ...st, displayBgColorPicker: !st.displayBgColorPicker }))}
                            >
                                <div
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: '2px',
                                        background: state.backgroundColour || '#5cb85c'
                                    }}
                                />
                            </div>
                            {state.displayBgColorPicker ? (
                                <div style={popover}>
                                    <div style={cover} onClick={() => setState(st => ({ ...st, displayBgColorPicker: false }))} />
                                    <SketchPicker
                                        width="241px"
                                        presetColors={presetColors}
                                        color={state.backgroundColour || '#5cb85c'}
                                        onChange={(updatedColor) =>
                                            setState((state) => ({
                                                ...state,
                                                backgroundColour: updatedColor.hex
                                            }))
                                        }
                                    />
                                </div>
                            ) : null}
                        </div>

                        <div style={{ flex: '0 0 140px', position: 'relative' }}>
                            <FormLabel style={{ display: 'block', float: 'none', marginBottom: '4px', fontSize: '0.75rem' }}>Text Color</FormLabel>
                            <div
                                style={{
                                    padding: '8px',
                                    background: '#fff',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    height: '38px',
                                    boxSizing: 'border-box'
                                }}
                                onClick={() => setState(st => ({ ...st, displayFgColorPicker: !st.displayFgColorPicker }))}
                            >
                                <div
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: '2px',
                                        background: state.foregroundColour || '#ffffff'
                                    }}
                                />
                            </div>
                            {state.displayFgColorPicker ? (
                                <div style={popover}>
                                    <div style={cover} onClick={() => setState(st => ({ ...st, displayFgColorPicker: false }))} />
                                    <SketchPicker
                                        width="241px"
                                        presetColors={presetColors}
                                        color={state.foregroundColour || '#ffffff'}
                                        onChange={(updatedColor) =>
                                            setState((state) => ({
                                                ...state,
                                                foregroundColour: updatedColor.hex
                                            }))
                                        }
                                    />
                                </div>
                            ) : null}
                        </div>

                        <div style={{ flex: '0 0 120px' }}>
                            <FormLabel style={{ display: 'block', float: 'none', visibility: 'hidden', marginBottom: '4px', fontSize: '0.75rem' }}>&nbsp;</FormLabel>
                            <SecondaryButton onClick={handleAddOrUpdateOption} className="setFullWidth" style={{ height: '38px', margin: 0 }}>
                                {state.isUpdate ? 'Update' : 'Add'}
                            </SecondaryButton>
                        </div>
                    </div>

                    <div
                        style={{
                            height: '300px',
                            width: '100%',
                            margin: '20px 0px 0px 0px'
                        }}
                    >
                        <DataGrid
                            components={{
                                NoRowsOverlay: CustomNoRowsOverlay
                            }}
                            headerHeight={50}
                            rowHeight={50}
                            hideFooter={true}
                            columns={columns}
                            rows={activeRows}
                            disableColumnMenu={true}
                            showColumnRightBorder={true}
                            pageSize={100}
                        />
                    </div>
                </DialogContents>
            </div>
        </Dialogs>
    );
};

export default WorkflowStepsListOptions;
